import { actions } from "../../app/state.js";
import { openCreateContactModal, openEditContactModal } from "./ContactsModal.js";
import { openCreateVenueModal, openEditVenueModal } from "./VenueModal.js";
import { fetchContact } from "./EventModal.data.js";
import { setContactPreview, setVenuePreview, setVenueDistancePreview, setEquipmentToggle, getEquipmentToggle } from "./EventModal.previews.js";
import { $, escapeHtml, normStr } from "./EventModal.utils.js";
import { getTierInfoForRegion } from "../../constants/regionTiers.js";

function wireContactCombobox(root, teamId, { loadAndFillContacts }) {
  const container = $(root, "#event-contact-combobox-wrapper");
  const searchInput = $(root, "#event-contact-search");
  const hiddenInput = $(root, "#event-contact-select");
  const dropdown = $(root, "#event-contact-dropdown");
  const list = $(root, "#event-contact-dropdown-list");
  const actionsContainer = $(root, "#event-contact-actions");
  const editBtn = $(root, "#event-contact-edit");

  if (!container || !searchInput || !hiddenInput || !dropdown || !list) {
    return () => {};
  }

  function closeDropdown() {
    dropdown.classList.add("hidden");
  }

  function openDropdown() {
    dropdown.classList.remove("hidden");
  }

  function updateActions() {
    if (!actionsContainer) return;
    if (hiddenInput.value) {
      actionsContainer.classList.remove("opacity-0");
      if (editBtn) editBtn.classList.remove("hidden");
    } else {
      actionsContainer.classList.add("opacity-0");
      if (editBtn) editBtn.classList.add("hidden");
    }
  }

  const normalizeText = window.CRM?.utils?.stringUtils?.normalizeSearch || ((str) => String(str || "").toLowerCase());

  function renderList(query = "") {
    const contacts = root._contactsData || [];
    const normalizedQuery = normalizeText(query);

    list.innerHTML = "";

    const matches = normalizedQuery
      ? contacts.filter((c) => {
          const full = [c.name, c.surname].filter(Boolean).join(" ").trim();
          const meta = c.company ? ` ${c.company}` : "";
          const email = c.email ? ` ${c.email}` : "";
          const phone = c.phone ? ` ${c.phone}` : "";
          return normalizeText(`${full}${meta}${email}${phone}`).includes(normalizedQuery);
        })
      : contacts;

    if (matches.length > 0) {
      matches.forEach((c) => {
        const li = document.createElement("li");
        const full = [c.name, c.surname].filter(Boolean).join(" ").trim();
        const meta = c.company ? ` \u00b7 ${c.company}` : "";
        li.className = "px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer flex flex-col";
        li.innerHTML = `
          <span class="font-medium">${escapeHtml(full || "\u2014")}</span>
          <span class="text-xs text-slate-500">${escapeHtml(c.email || c.phone || "")}${escapeHtml(meta)}</span>
        `;
        li.addEventListener("mousedown", async (e) => {
          e.preventDefault();
          hiddenInput.value = c.id;
          searchInput.value = `${full || "\u2014"}${meta}`;
          updateActions();
          closeDropdown();

          try {
            const fetchedContact = await fetchContact(c.id);
            setContactPreview(root, fetchedContact);
          } catch (err) {
            console.error(err);
          }
        });
        list.appendChild(li);
      });
      return;
    }

    const li = document.createElement("li");
    li.className = "px-3 py-3 text-sm text-emerald-600 font-semibold hover:bg-emerald-50 cursor-pointer flex items-center gap-2 border-t border-slate-100";
    li.innerHTML = `<i class="fa-solid fa-plus uppercase"></i> Crear nuevo "${escapeHtml(query)}"`;
    li.addEventListener("mousedown", (e) => {
      e.preventDefault();
      closeDropdown();

      setTimeout(() => {
        openCreateContactModal({
          onSaved: async (created) => {
            if (!created?.id) return;
            try {
              await loadAndFillContacts(root, teamId, { selectId: created.id });
              wireContactCombobox(root, teamId, { loadAndFillContacts });
              actions.addToast?.({ type: "success", message: "Contacto seleccionado" });
              updateActions();
            } catch (err) {
              actions.addToast?.({ type: "error", message: String(err?.message || err) });
            }
          },
        });

        setTimeout(() => {
          const rootModal = document.querySelector("#contacts-modal-template[data-is-editable]");
          if (!rootModal) return;
          const nameInput = rootModal.querySelector("#contact-name");
          if (nameInput) nameInput.value = query;
        }, 100);
      }, 0);
    });
    list.appendChild(li);
  }

  searchInput.addEventListener("focus", () => {
    if (root.getAttribute("data-is-editable") !== "true") return;
    renderList(searchInput.value);
    openDropdown();
  });

  searchInput.addEventListener("input", (e) => {
    if (root.getAttribute("data-is-editable") !== "true") return;
    hiddenInput.value = "";
    setContactPreview(root, null);
    updateActions();
    renderList(e.target.value);
    openDropdown();
  });

  document.addEventListener("mousedown", (e) => {
    if (!container.contains(e.target)) closeDropdown();
  });

  updateActions();
  return updateActions;
}

export async function wireSelectors(root, teamId, deps) {
  const {
    loadAndFillContacts,
    loadAndFillEncargados,
    loadAndFillVenues,
    loadAndFillRegions,
    updateForecast,
    renderPriceHint,
    refreshVenuePriceForEquipment,
    wireVenueCombobox,
    renderDateConflicts,
  } = deps;

  let conflictRenderTimer = null;
  const refreshDateConflicts = () => {
    window.clearTimeout(conflictRenderTimer);
    conflictRenderTimer = window.setTimeout(() => {
      const eventId = $(root, "#event-modal-id")?.textContent?.trim();
      const dateValue = $(root, "#event-date")?.value;
      renderDateConflicts(root, dateValue, eventId !== "\u2014" ? eventId : null, teamId);
    }, 250);
  };

  await loadAndFillContacts(root, teamId);
  await loadAndFillEncargados(root, teamId);

  const updateContactActions = wireContactCombobox(root, teamId, { loadAndFillContacts });

  $(root, "#event-contact-clear")?.addEventListener("click", () => {
    const contactInput = $(root, "#event-contact-select");
    const contactSearch = $(root, "#event-contact-search");
    if (contactInput) contactInput.value = "";
    if (contactSearch) contactSearch.value = "";
    setContactPreview(root, null);
    updateContactActions();
  });

  await loadAndFillVenues(root, teamId);
  wireVenueCombobox(root, teamId);

  $(root, "#event-venue-clear")?.addEventListener("click", () => {
    const venueInput = $(root, "#event-venue-select");
    const venueSearch = $(root, "#event-venue-search");
    if (venueInput) venueInput.value = "";
    if (venueSearch) venueSearch.value = "";
    setVenuePreview(root, null);
    setVenueDistancePreview(root, null);
    root._lastVenueKm = null;
    root._lastVenueRegion = null;
  });

  $(root, "#event-contact-create")?.addEventListener("click", () => {
    openCreateContactModal({
      onSaved: async (created) => {
        if (!created?.id) return;
        try {
          await loadAndFillContacts(root, teamId, { selectId: created.id });
          wireContactCombobox(root, teamId, { loadAndFillContacts });
          actions.addToast?.({ type: "success", message: "Contacto seleccionado" });
          updateContactActions();
        } catch (e) {
          actions.addToast?.({ type: "error", message: String(e?.message || e) });
        }
      },
    });
  });

  $(root, "#event-contact-edit")?.addEventListener("click", () => {
    const contactSelect = $(root, "#event-contact-select");
    const id = normStr(contactSelect?.value);
    if (!id) {
      actions.addToast?.({ type: "info", message: "Selecciona un contacto para editar." });
      return;
    }
    openEditContactModal(id, {
      onSaved: async (updated) => {
        if (!updated?.id) return;
        try {
          await loadAndFillContacts(root, teamId, { selectId: updated.id });
        } catch (e) {
          actions.addToast?.({ type: "error", message: String(e?.message || e) });
        }
      },
    });
  });

  $(root, "#event-venue-create")?.addEventListener("click", () => {
    openCreateVenueModal({
      teamId,
      onSaved: async (created) => {
        if (!created?.id) return;
        try {
          await loadAndFillVenues(root, teamId, { selectId: created.id });
          wireVenueCombobox(root, teamId);
          actions.addToast?.({ type: "success", message: "Venue seleccionado" });
        } catch (e) {
          actions.addToast?.({ type: "error", message: String(e?.message || e) });
        }
      },
    });
  });

  $(root, "#event-venue-edit")?.addEventListener("click", () => {
    const venueSelect = $(root, "#event-venue-select");
    const id = normStr(venueSelect?.value);
    if (!id) {
      actions.addToast?.({ type: "info", message: "Selecciona un venue para editar." });
      return;
    }

    openEditVenueModal(id, {
      teamId,
      onSaved: async () => {
        try {
          await loadAndFillVenues(root, teamId, { selectId: id });
        } catch (e) {
          actions.addToast?.({ type: "error", message: String(e?.message || e) });
        }
      },
    });
  });

  await loadAndFillRegions(root);
  $(root, "#event-region")?.addEventListener("change", (e) => {
    // Default de "incluye equipo" segun el tramo de la nueva region, ANTES
    // de calcular el hint (que depende del estado del switch). El usuario
    // puede desmarcarlo/marcarlo a mano despues sin problema; solo se
    // re-propone al cambiar de region explicitamente.
    setEquipmentToggle(root, Boolean(getTierInfoForRegion(e.target.value)?.productionIncluded));

    updateForecast(root, e.target.value);
    refreshDateConflicts();
  });

  // Switch "Incluye equipo": boton propio (no checkbox nativo) para evitar
  // el problema reportado con checkboxes en esta app.
  $(root, "#event-equipment-included")?.addEventListener("click", (e) => {
    setEquipmentToggle(root, !getEquipmentToggle(root));
    // Baseline por tramo; si hay un venue con km ya calculado,
    // refreshVenuePriceForEquipment lo sustituye por el precio exacto.
    renderPriceHint(root, $(root, "#event-region")?.value || "");
    refreshVenuePriceForEquipment(root, teamId);
  });

  const stageSel = $(root, "#event-stage");
  const reasonCont = $(root, "#event-lost-reason-container");
  if (stageSel && reasonCont) {
    stageSel.addEventListener("change", () => {
      reasonCont.classList.toggle("hidden", stageSel.value !== "rechazado");
    });
  }

  const dateInput = $(root, "#event-date");
  if (dateInput) {
    dateInput.addEventListener("change", () => {
      refreshDateConflicts();
    });
  }

  ["#event-name", "#event-price", "#event-type", "#event-stage", "#event-venue-select"].forEach((selector) => {
    const el = $(root, selector);
    if (!el) return;
    el.addEventListener("input", refreshDateConflicts);
    el.addEventListener("change", refreshDateConflicts);
  });
}
